const BORDER_NONE_IMPORTANT = 'none !important';

const theme = {
  typography: {
    htmlFontSize: 14,
    fontFamily: 'Inter, Arial, sans-serif',
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: {
      fontSize: '24px',
      fontWeight: 600,
      lineHeight: '32px',
    },
    h2: {
      fontSize: '22px',
      fontWeight: 500,
      lineHeight: '32px',
    },
    h3: {
      fontSize: '20px',
      fontWeight: 600,
      lineHeight: '32px',
    },
    h3_2: { fontSize: '20px', fontWeight: 500, lineHeight: '30px' },
    h4: {
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: '24px',
    },
    h5: {
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: '24px',
    },
    h6: {
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: 'normal',
    },
    h7: {
      fontSize: '20px',
      fontWeight: 600,
      lineHeight: '28px',
      letterSpacing: '0.05px',
    },
    h8: {
      fontSize: '15px',
      fontWeight: 600,
      lineHeight: 'normal',
    },
    subtitle1: {
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '20px',
    },
    subtitle2: {
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '30px',
    },
    subtitle3: {
      fontSize: '18px',
      fontWeight: 600,
      lineHeight: '27px',
    },
    subtitle4: {
      fontSize: '18px',
      fontWeight: 500,
      lineHeight: '27px',
    },
    body1: {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '20px',
    },
    body2: {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '16px',
    },
    body3: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '18px',
      letterSpacing: '0px',
    },
    body3Semibold: {
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: '16.8px',
      letterSpacing: '0.03px',
    },
    button: {
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '20px',
      textTransform: 'none',
    },
    caption: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '16px',
    },
    // Custom variants
    captionMedium: {
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: '16px',
    },
    captionBold: {
      fontSize: '12px',
      fontWeight: 700,
      lineHeight: '16px',
    },
    small: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '12px',
    },
    tiny: {
      fontSize: '10px',
      fontWeight: 400,
      lineHeight: '16px',
    },
    micro: {
      fontSize: '8px',
      fontWeight: 500,
      lineHeight: '12px',
    },
    captionSemibold: {
      fontSize: '11px',
      fontWeight: 600,
    },
    sectionHeader: {
      fontSize: '10px',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.8px',
    },
    smallCaption: {
      fontSize: '10px',
      fontWeight: 600,
      lineHeight: 1,
    },
    body4: {
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '140%',
      letterSpacing: '0.25px',
    },
    body5: {
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: '130%',
      letterSpacing: '0.12px',
    },
    body6: {
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '16px',
    },
    body7: {
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: '16px',
    },
    body8: {
      fontSize: '14px',
      fontWeight: 700,
      lineHeight: '16px',
    },
    body10: {
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: '15px',
    },
    attributeLabel: {
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: '18px',
    },
    attributeValue: {
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '21px',
    },
    body9: {
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: '20px',
    },
    // Tight, single-line rows (avatar name/meta rows) use a unitless
    // lineHeight (matches the existing `smallCaption` precedent) instead of
    // an explicit px value, since they're vertically centered by flexbox
    // rather than relying on line-box height.
    compactMedium: {
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: 1,
    },
    compactRegular: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: 1,
    },
    compactRegularLoose: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: 1.4,
    },
    microLabel: {
      fontSize: '11px',
      fontWeight: 400,
      lineHeight: '16px',
    },
    bodyDense: {
      fontSize: '13px',
      fontWeight: 400,
      lineHeight: '20px',
    },
  },
  zIndex: {
    appBar: 20,
  },
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      // most basic recommended timing
      standard: 300,
      // this is to be used in complex animations
      complex: 375,
      // recommended when something is entering screen
      enteringScreen: 225,
      // recommended when something is leaving screen
      leavingScreen: 195,
    },
    easing: {
      // This is the most common easing curve.
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      // Objects enter the screen at full velocity from off-screen and
      // slowly decelerate to a resting point.
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      // Objects leave the screen at full velocity. They do not decelerate when off-screen.
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      // The sharp curve is used by objects that may return to the screen at any time.
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':before, :after': {
          boxSizing: 'border-box',
        },
        '*': {
          boxSizing: 'border-box',
          '&:focus': {
            outline: 'none',
          },
          // Scrollbar styles
          '&::-webkit-scrollbar': {
            width: '4px',
            height: '4px',
            transition: '0.2s',
          },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: '10px',
            opacity: 0.5,
          },
          '&::-webkit-scrollbar-track': {
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-corner': {
            backgroundColor: 'transparent',
          },
          '.ReactMuiTableToolbarFilter-inputInput': {
            padding: '10px 56px 10px 8px',
            transition: 'width 0.4s ease-in-out',
          },
        },
        '.hover-scrollbar:not(.is-visible)::-webkit-scrollbar-thumb': {
          backgroundColor: 'transparent',
        },

        '.custom-link:hover': {
          cursor: 'pointer',
        },

        '.hover-underline-link': {
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
            cursor: 'pointer',
          },
        },

        '.breadcrumb-link:hover': {
          textDecoration: 'underline',
          color: 'inherit',
        },

        '.breadcrumb-link:active, .custom-link:active': {
          color: 'inherit',
        },

        '.breadcrumb-link': {
          textDecoration: 'none',
          color: 'inherit',
        },

        '.table-card-wrapper': {
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '4px',
          overflow: 'hidden',
          paddingTop: '8px',
          paddingBottom: '8px',
          paddingLeft: '8px',
          boxShadow: 'none !important',
        },

        // AI Autofill processing toast (structural styles)
        '.ai-autofill-notification': {
          '& .title': {
            cursor: 'move',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 12px',
            borderRadius: '4px 4px 0 0',
          },
          '& .title-content': {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            minWidth: 0,
          },
          '& .title-text': {
            color: 'inherit',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
          '& .dismiss': {
            width: '24px',
            height: '24px',
            borderRadius: '4px',
          },
          '& .progress': {
            height: '3px',
            borderRadius: 0,
          },
          '& .body': {
            padding: '12px 16px',
            display: 'flex',
            gap: '12px',
            alignItems: 'flex-start',
          },
          '& .icon': {
            flexShrink: 0,
            width: '36px',
            height: '36px',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
          '& .content': {
            flex: 1,
            minWidth: 0,
          },
          '& .file-name': {
            display: 'block',
            maxWidth: '100%',
            width: '100%',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
          '& .subtitle': {
            display: 'block',
            marginTop: '2px',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          },
          '& .actions': {
            display: 'flex',
            gap: '8px',
            padding: '0 16px 12px',
          },
          '& .actions .action': {
            flex: 1,
            textTransform: 'none',
          },
          '& .actions .view-suggestions, & .actions .retry': {
            fontWeight: 600,
          },
        },
        '@keyframes aiAutofillToastPulse': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.5 },
        },
        '.ai-autofill-notification .status-icon.pulse': {
          animation: 'aiAutofillToastPulse 1.5s ease-in-out infinite',
        },

        //common select box styles
        '.typeFilterChip': {
          height: '36px',
          cursor: 'pointer',
        },
        '.logFilterChip, .MuiButtonBase-root.logFilterChip': {
          height: '36px',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          padding: '10px 5px',
          borderRadius: '4px',
          whiteSpace: 'nowrap',
          width: 'fit-content',
          border: '1px solid',
        },

        // Selected entities  wrapper and chips
        '.selected-entities': {
          display: 'flex',
          flexWrap: 'nowrap',
          gap: '4px',
          width: 'auto',
          maxWidth: '90%',
          flexShrink: 0,
          alignItems: 'center',
        },
        '.selected-entities .selected-entity-chip': {
          flexShrink: 0,
          position: 'relative',
          top: '-1px',
        },
        '.selected-entities .selected-more-chip': {
          flexShrink: 0,
          cursor: 'pointer',
        },
        // Popover content for selected entities list
        '.selected-entities-popover': {
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px',
          padding: '12px',
          maxWidth: 395,
          maxHeight: 200,
          overflowY: 'auto',
        },

        // Autocomplete end adornment positioning helper
        '.autocomplete': {
          '& .MuiAutocomplete-endAdornment': {
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
          },
        },
        // HTML root styles
        html: {
          fontSize: '100%',
          overflowY: 'hidden',
          scrollBehavior: 'smooth',

          // Table resizer styles
          '& .resizer': {
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: '14px',
            cursor: 'col-resize',
            touchAction: 'none',
            zIndex: 2,
          },

          '& .resizer::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '1px',
          },
        },

        // Body styles
        body: {
          margin: 0,
          fontFamily: "'Inter', sans-serif !important",
          '& iframe': {
            position: 'relative !important',
          },

          // Input number spinner removal
          '& input[type="number"]::-webkit-inner-spin-button, & input[type="number"]::-webkit-outer-spin-button':
            {
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              appearance: 'none',
              margin: 0,
            },
        },

        // Hide password reveal button in MS browsers
        'input::-ms-reveal, input::-ms-clear': {
          display: 'none',
        },

        // Blinker animation
        '@keyframes blinker': {
          '50%': {
            borderColor: '#2f81f7',
            boxShadow: '0 0 1px 1px #2f81f7',
          },
        },

        '.blink_me': {
          animation: 'blinker 1s linear infinite',
        },

        // Linked entity button pulse animation
        '@keyframes linkedEntityBtnPulse': {
          '0%, 100%': {
            boxShadow: '0 0 0 0 transparent',
            opacity: 0,
          },
          '50%': {
            opacity: 1,
          },
        },

        '.linked-entity-btn-pulse': {
          position: 'relative',
          display: 'inline-flex',
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: '-6px',
            borderRadius: '8px',
            pointerEvents: 'none',
            animation: 'linkedEntityBtnPulse 1s ease-in-out 3',
          },
        },

        // React Query Builder styles
        '.queryBuilder': {
          minWidth: '100%',
        },
        '.ruleGroup': {
          border: '0.5px solid',
          gap: '16px !important',
          padding: '16px',
          borderRadius: '8px',
        },
        '.ruleGroup-header': {
          gap: '16px !important',
        },
        '.ruleGroup-body': {
          maxWidth: '100%',
        },
        '.queryBuilder-branches .rule::before': {
          left: 'calc(-0.5rem - 5px) !important',
        },
        '.queryBuilder-branches .rule::after': {
          left: 'calc(-0.5rem - 5px) !important',
        },
        '.rule': {
          padding: '10px 8px',
          borderRadius: '8px',
          borderLeft: '4px solid',
          display: 'flex',
          alignItems: 'center',
          gap: '16px !important',
        },

        // Responsive styles for smaller containers (Filter.jsx and CreateFilterDrawer.jsx)
        '.advance-query-builder': {
          '& .ruleGroupAdvance': {
            padding: '1px !important',
            gap: '0px !important',
            position: 'relative',
            width: '100%',
            border: 'none !important',
            boxShadow: 'none !important',
          },
          '& .ruleAdvance': {
            gap: '3.5px !important',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            overflowX: 'auto',
            minWidth: '100px',
            position: 'relative',
            border: 'none !important',
            boxShadow: 'none !important',
            '& .MuiInputBase-root': {
              padding: '6px 4px !important',
              maxWidth: '160px',
              width: '100%',
              height: '40px !important',
              boxSizing: 'border-box',
            },
          },
          '& .ruleGroupAdvance-body': {
            maxWidth: '100% !important',
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          },
          '& .ruleAdvance-value': {
            maxWidth: '10px !important',
            minWidth: '10px !important',
            flexGrow: 1,
            flexShrink: 1,
            '& .MuiFormControl-root': {
              marginTop: '0 !important',
              maxWidth: '10px !important',
              minWidth: '10px !important',
            },
          },
          '& .ruleAdvance-operators': {
            minWidth: '55px !important', // Reduced min width
            maxWidth: '70px !important', // Reduced max width
            '& .MuiInputBase-root': {
              maxWidth: '100%',
              fontSize: '11px !important', // Even smaller font
            },
          },
          '& .ruleAdvance-fields': {
            minWidth: '70px !important', // Reduced min width
            maxWidth: '85px !important', // Reduced max width
            '& .MuiInputBase-root': {
              maxWidth: '100%',
            },
          },

          // Allow horizontal scrolling on small screens
          overflowX: 'auto',
          '.compactAdvance-value': {
            border: 'none !important',
            background: 'transparent !important',
          },
        },
        // Conditional query builder styles (for AttributeLibrary conditional formulas)
        '.conditional-query-builder': {
          '& .conditionalRuleGroup': {
            padding: '1px !important',
            gap: '0px !important',
            position: 'relative',
            width: '100%',
            border: 'none !important',
            boxShadow: 'none !important',
            backgroundColor: 'transparent !important',
          },
          '& .conditionalRule': {
            gap: '3.5px !important',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            overflowX: 'auto',
            minWidth: '100px',
            position: 'relative',
            border: 'none !important',
            boxShadow: 'none !important',
            backgroundColor: 'transparent !important',
            '& .MuiInputBase-root': {
              padding: '6px 4px !important',
              maxWidth: '160px',
              width: '100%',
              height: '40px !important',
              boxSizing: 'border-box',
            },
          },
          '& .conditionalRule-operators': {
            minWidth: '55px !important',
            maxWidth: '70px !important',
            '& .MuiInputBase-root': {
              maxWidth: '100%',
              fontSize: '11px !important',
            },
          },
          '& .conditionalRule-fields': {
            minWidth: '70px !important',
            maxWidth: '85px !important',
            '& .MuiInputBase-root': {
              maxWidth: '100%',
            },
          },
          '& .conditionalRule-value': {
            maxWidth: '10px !important',
            minWidth: '10px !important',
            flexGrow: 1,
            flexShrink: 1,
            '& .MuiFormControl-root': {
              marginTop: '0 !important',
              maxWidth: '10px !important',
              minWidth: '10px !important',
            },
          },
        },
        // Responsive styles for smaller containers (Filter.jsx and CreateFilterDrawer.jsx)
        '.compact-query-builder': {
          '& .ruleGroup': {
            padding: '8px !important',
            gap: '8px !important',
            position: 'relative', // Ensure proper positioning context
            width: '100%', // Ensure the container uses full width
          },
          '& .ruleGroup-body': {
            maxWidth: '100% !important',
            overflowX: 'auto',
          },
          '& .rule': {
            padding: '6px 4px !important',
            gap: '16px !important',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            overflowX: 'auto',
            width: '100%',
            position: 'relative', // For proper positioning
            marginBottom: '4px', // Add spacing between rules
            alignItems: 'flex-end !important', // Align items to the end
          },
          '& .rule-value': {
            maxWidth: '110px !important', // Reduced max width
            minWidth: '70px !important', // Reduced min width
            flexGrow: 1,
            flexShrink: 1,
            '& .MuiFormControl-root': {
              marginTop: '0 !important',
              maxWidth: '100%',
            },
          },
          '& .rule-operators': {
            minWidth: '55px !important', // Reduced min width
            maxWidth: '70px !important', // Reduced max width
            '& .MuiInputBase-root': {
              maxWidth: '100%',
              fontSize: '11px !important', // Even smaller font
            },
          },
          '& .rule-fields': {
            minWidth: '70px !important', // Reduced min width
            maxWidth: '85px !important', // Reduced max width
            '& .MuiInputBase-root': {
              maxWidth: '100%',
            },
          },

          // Allow horizontal scrolling on small screens
          overflowX: 'auto',
        },

        // Custom styles for the rule operators
        '.rule-operators': {
          '& div': {
            width: 'fit-content',
          },
        },

        '.simple-query-wrapper': {
          '& .ruleGroupSimple > .ruleGroup-header': {
            display: 'none !important',
            padding: 0,
            margin: 0,
            minHeight: 0,
          },
          '& .ruleGroupSimple': {
            border: 'none !important',
            padding: '0px !important',
          },
          '& .ruleSimple': {
            gap: '8px',
            flexDirection: 'row !important',
            flexWrap: 'nowrap !important',
            overflowX: 'hidden !important',
            border: 'none !important',

            boxShadow: 'none !important',
            minWidth: 0,

            '& .MuiFormControl-root': {
              flex: 1,
              minWidth: 0,
            },

            '& .MuiInputBase-root': {
              width: '100%',
              minWidth: 0,
              height: '40px !important',
              padding: '2px 2px !important',
            },
          },

          '& .ruleGroupSimple-body': {
            maxWidth: '100% !important',
            overflowX: 'auto',
          },

          '& .ruleSimple-value': {
            maxWidth: '190px !important',
            minWidth: '190px !important',
            flexShrink: 1,
            '& .MuiFormControl-root': {
              marginTop: '0 !important',
              maxWidth: '100% !important',
              minWidth: '100% !important',
              height: '40px !important',
              boxSizing: 'border-box',
              minHeight: '40px !important',
              '& .MuiInputBase-root': {
                height: '40px !important',
                fontWeight: 400,
                fontSize: '14px',
                padding: '10px 14px !important',
              },
              '& input': {
                height: '100% !important',
                padding: '0 !important',
              },
            },
          },
          '& .rule-operatorsSimple': {
            minWidth: '55px !important',
            maxWidth: '70px !important',
            '& .MuiInputBase-root': {
              maxWidth: '100%',
              fontSize: '11px !important',
            },
          },
          '& .rule-fieldsSimple': {
            minWidth: '70px !important',
            maxWidth: '85px !important',
            height: '40px  !important',
            '& .MuiInputBase-root': {
              maxWidth: '100%',
            },
          },
          overflowX: 'auto',
          '& .compact-valueSimple': {
            border: 'none !important',
            background: 'transparent !important',
          },
        },

        '.compact-rule-value': {
          maxWidth: '170px !important',
          minWidth: '170px !important',
          flexShrink: 1,
          '.MuiAutocomplete-root .MuiAutocomplete-inputRoot.MuiInputBase-sizeSmall .MuiAutocomplete-input':
            {
              padding: '0.5px 38px 0.5px 4px !important',
            },
          '.MuiFormControl-root': {
            marginTop: '0 !important',
            maxWidth: '100% !important',
            minWidth: '100% !important',
            height: '40px !important',
            boxSizing: 'border-box',
            minHeight: '40px !important',
          },
          '.MuiAutocomplete-inputRoot .MuiInputBase-input': {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
            paddingRight: '48px !important',
            paddingTop: '2px',
          },
          '.MuiFormControl-root .MuiInputBase-root': {
            height: '40px !important',
            boxSizing: 'border-box',
            fontWeight: 400,
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '1.4',
            letterSpacing: '0.25%',
            paddingLeft: '8px !important',
            paddingRight: '10px !important',
            minWidth: 0,
          },
          '.MuiInputBase-root.MuiOutlinedInput-root .MuiSelect-select': {
            paddingTop: '8px !important',
            paddingBottom: '8px !important',
          },

          '.MuiAutocomplete-inputRoot.MuiInputBase-sizeSmall': {
            minHeight: '40px !important',
          },

          '.MuiAutocomplete-inputRoot': {
            minHeight: '40px !important',
          },
          '.MuiInputBase-root': {
            minHeight: '36px !important',
            boxSizing: 'border-box',
            fontSize: '14px',
          },
          '.MuiInputBase-root:not(.MuiAutocomplete-inputRoot) .MuiInputBase-input':
            {
              padding: '16px 4px !important',
            },
          '.MuiAutocomplete-input': {
            paddingTop: '6px !important',
            textOverflow: 'ellipsis',
            fontSize: '14px',
            whiteSpace: 'nowrap',
            borderRadius: '4px',
          },
        },

        '.schema-tabs-root': {
          minHeight: 44,
          '& .MuiTab-root': {
            minHeight: 44,
            fontSize: '13px',
            fontWeight: 500,
            textTransform: 'none',
          },
          '& .MuiTabs-indicator': {
            height: 2,
            borderRadius: '2px 2px 0 0',
          },
        },
        '.schema-tab-panel': {
          flex: 1,
          height: '100%',
          overflow: 'hidden',
          flexDirection: 'column',
        },
        '.advance-header-input .MuiOutlinedInput-root': {
          borderRadius: '4px',
          fontSize: '14px',
          fontWeight: '400',
          width: '150px',
          padding: '9.5px 0px 4.5px 8px',
          marginTop: -22,
          '& fieldset': {
            border: 'none',
            height: '10px',
          },

          '&:hover fieldset': {
            border: 'none',
          },

          '&.Mui-focused fieldset': {
            border: 'none',
          },

          '& .MuiInputBase-input': {
            fontSize: '14px',
            fontWeight: '400',
            height: '20px',
            padding: '9.5px 0px 4.5px 8px',
          },
        },

        '.editor-toolbar': {
          display: 'flex',
          alignItems: 'start',
          minHeight: '40px',
        },
        // Tip tab editor styles
        '.tiptap:focus-visible': {
          outline: 'none',
        },
        '.tiptap:focus': {
          outline: 'none',
        },
        '.ProseMirror-focused': {
          outline: 'none',
        },
        '.tiptap ul, .tiptap ol': {
          paddingInlineStart: '20px',
        },
        '.tiptap pre': {
          borderRadius: '0.5rem',
          fontFamily: 'monospace !important',
          margin: '1.5rem 0',
          padding: '0.75rem 1rem',
        },
        '.tiptap code': {
          borderRadius: '0.4rem',
          fontSize: '0.85rem',
          padding: '0.25em 0.3em',
          fontFamily: 'monospace !important',
        },
        '.tiptap pre code': {
          background: 'none',
          color: 'inherit',
          fontSize: '0.8rem',
          padding: 0,
        },
        '.tiptap blockquote': {
          margin: '1.5rem 0',
          paddingLeft: '1rem',
        },
        '.tiptap hr': {
          border: 'none',
          margin: '2rem 0',
        },
        '.tiptap img': {
          width: '100%',
          margin: '0 auto',
        },
        '.tiptap table': {
          borderCollapse: 'collapse',
          margin: 0,
          overflow: 'hidden',
          tableLayout: 'fixed',
          width: '100%',
        },
        '.tiptap td, .tiptap th': {
          boxSizing: 'border-box',
          minWidth: '1em',
          padding: '6px 8px',
          position: 'relative',
          verticalAlign: 'top',
        },
        '.tiptap td > *, .tiptap th > *': {
          marginBottom: 0,
        },
        '.tiptap th': {
          fontWeight: 'bold',
          textAlign: 'left',
        },
        '.tiptap .selectedCell::after': {
          content: '""',
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          pointerEvents: 'none',
          position: 'absolute',
          zIndex: 2,
        },
        '.tiptap .column-resize-handle': {
          bottom: '-2px',
          pointerEvents: 'none',
          position: 'absolute',
          right: '-1px',
          top: 0,
          width: '4px',
        },
        '.tiptap .tableWrapper': {
          margin: '1.5rem 0',
          overflowX: 'auto',
        },
        '.tiptap.resize-cursor': {
          cursor: 'ew-resize',
        },
        '.tiptap p.is-editor-empty:first-child::before': {
          float: 'left',
          height: 0,
          pointerEvents: 'none',
        },
        // Table schema resize handler styles
        '.resize-handle': {
          opacity: 0,
          zIndex: 2,
          transition: 'opacity 0.2s',
          position: 'absolute !important',
          width: '4px !important',
          height: '100% !important',
          right: '-2px !important',
          top: '0 !important',
          cursor: 'col-resize',
          backgroundColor: 'transparent',
        },
        '.resize-handle:hover': {
          opacity: 1,
        },
        '.resize-handle:active': {
          opacity: '1 !important',
        },
        '.react-resizable': {
          position: 'relative',
          width: '100%',
          height: '100%',
        },
        '.landscapeNodeTitle': {
          position: 'absolute',
          top: '-20px',
          right: '-90px',
          width: '100px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        // ReactFlow Landscape – edge label overlay and canvas panel structural styles
        '.landscape-edge-label': {
          position: 'absolute',
          fontSize: '12px',
          pointerEvents: 'none',
          padding: '2px 4px',
          borderRadius: '4px',
          zIndex: 2000,
          transition: 'opacity 0.2s',
        },
        '.landscape-panel': {
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          padding: '4px 0px',
          height: '44px',
          boxSizing: 'border-box',
          borderRadius: '4px',
          backdropFilter: 'blur(8px) saturate(180%)',
          WebkitBackdropFilter: 'blur(8px) saturate(180%)',
        },
        // RichEditor styles
        '& .DraftEditor-root': {
          background: 'transparent',
        },
        '& .RichEditor-editor': {
          cursor: 'text',
          fontSize: '16px',
          marginTop: '10px',
        },

        '& .RichEditor-editor .public-DraftEditorPlaceholder-root': {
          margin: '0 - 15px - 15px',
          padding: '15px',
        },
        '& .RichEditor-editor .public-DraftEditor-content': {
          margin: '0 - 15px - 15px',
          minHeight: '100px',
        },
        '& .RichEditor-hidePlaceholder .public-DraftEditorPlaceholder-root': {
          display: 'none',
        },
        '& .RichEditor-editor .RichEditor-blockquote': {
          fontFamily: 'Hoefler Text, Georgia, serif',
          fontStyle: 'italic',
          margin: '16px 0',
          padding: '10px 20px',
        },
        '& .RichEditor-editor .public-DraftStyleDefault-pre': {
          fontFamily: 'Inconsolata, Menlo, Consolas, monospace',
          fontSize: '16px',
          padding: '20px',
        },
        '& .RichEditor-controls': {
          fontFamily: 'Helvetica, sans-serif',
          fontSize: '14px',
          marginBottom: '5px',
          userSelect: 'none',
        },
        '& .RichEditor-styleButton': {
          cursor: 'pointer',
          marginRight: '16px',
          padding: '2px 0',
          display: 'inline-block',
        },
        '.RichEditor-root': {
          fontSize: '14px',
        },
        '.RichEditorOuterBox': {
          width: '100%',
          borderRadius: '4px',
        },
        '&.assign-user-input': {
          marginTop: 0,

          '& .MuiInputBase-root': {
            height: '36px',
            minHeight: '36px',
          },

          '& .MuiInputBase-input': {
            padding: '6px 8px',
            fontSize: '14px',
          },

          '& .MuiInputBase-input::placeholder': {
            fontSize: '14px',
            opacity: 0.7,
          },
        },
        '.auto-height-autocomplete': {
          '& .MuiInputBase-root': {
            height: 'auto !important',
            flexWrap: 'wrap',
          },
        },
        // statusPlaning styles

        '.statusTaskCardColumn': {
          borderRadius: '8px',
          width: '255px',
          padding: '8px',
        },
        // Bulk actions
        '.bulk-container': {
          borderRadius: '4px',
          overflow: 'auto',
        },
        // Help and support card
        '.help-support-article-card': {
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'transparent',
          ':hover': {
            borderWidth: '1px',
            borderStyle: 'solid',
          },
        },
        // Theme and appearance
        '.uploadedLogoPreview': {
          borderRadius: '4px !important',
        },
        '.logoDropzone': {
          borderRadius: '4px',
          outline: 'none',
          pt: '27px !important',
          '&:focus': {
            border: `1px solid`,
          },
        },
        // Alerts drawer list
        '.alert-drawer-list': {
          '&:hover': {
            cursor: 'pointer',
          },
          '&:focus-visible': {
            borderRadius: '4px',
            outline: 'none',
          },
        },
        // Approval list
        '.approval-list': {
          '&:hover': {
            cursor: 'pointer',
          },
          '&:focus-visible': {
            borderRadius: '4px',
            outline: 'none',
          },
        },
        // chatbot
        '.citationBox': {
          borderRadius: '8px',
          padding: '8px',
          position: 'relative',
          p: 1,
          maxWidth: '100%',
        },
        '.messageBox': {
          borderRadius: '8px',
          padding: '4px',
          position: 'relative',
          p: 1,
          maxWidth: '80%',
        },
        '.permissionButtons': {
          '&.inactive': {
            opacity: 0.6,
          },
        },
        '.infoTextStyle': {
          cursor: 'pointer',
          opacity: 0.8,
          '&:hover': {
            opacity: 1,
          },
        },
        // Automation
        '.automation-action-component, .automation-trigger-component, .automation-condition-component, .automation-loop-component':
          {
            borderWidth: '1px',
            borderStyle: 'solid',
            borderRadius: '4px',
            boxSizing: 'border-box',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            minHeight: '69px',
            padding: '16px',
            width: '100%',
          },
        '.automation-action-component.is-draggable': {
          cursor: 'grab',
        },
        '.automation-node-icon': {
          width: 32,
          height: 32,
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        },
        '.automation-node-icon svg': {
          display: 'block',
        },
        '.automation-node-content': {
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          minWidth: 0,
          flex: 1,
        },
        '.automation-node-icon-button svg': {
          width: 18,
          height: 18,
        },
        // DraggableErrorDialog
        '.draggable-error-dialog': {
          zIndex: 20000,
          position: 'fixed',
        },
        // Draggable List Item
        'li.draggable-list-item': {
          cursor: 'grab',
          userSelect: 'none',
          border: '1px solid',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px',
          marginBottom: '8px',
          transition:
            'background-color 0.2s ease-in-out, opacity 0.2s ease-in-out',
        },
        'li.draggable-list-item.is-dragging': {
          cursor: 'grabbing',
          opacity: 0.5,
        },
        'li.draggable-list-item .reorder-icon': {
          opacity: 0,
          transition: 'opacity 0.2s ease-in-out',
          marginRight: '8px',
        },
        'li.draggable-list-item:hover .reorder-icon': {
          opacity: 1,
        },
        '.react-flow__node-group': {
          padding: '0 !important',
          borderRadius: '4px !important',
          fontSize: 'inherit !important',
          color: 'inherit !important',
          textAlign: 'left !important',
          borderWidth: '0 !important',
          borderStyle: 'none !important',
          borderColor: 'transparent !important',
        },
        '.cellCommonProperty': {
          lineHeight: 1.3,
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          wordBreak: 'initial',
          whiteSpace: 'nowrap',
          minWidth: 0,
        },
        // Lets the chat-note count badge (which overlaps the icon's
        // top-right corner) render fully instead of being clipped by
        // the table cell's default overflow: hidden.
        '.chat-badge-cell .cellCommonProperty': {
          overflow: 'visible',
        },
        '.messageEditor': {
          fontWeight: '400 !important',
          fontSize: '14px !important',
          lineHeight: '1.6 !important',
        },
        '.aiButton': {
          minWidth: 'auto',
          padding: '6px 16px',
          textTransform: 'none',
          borderRadius: '6px',
          fontWeight: 600,
          whiteSpace: 'nowrap',
          flexShrink: 0,
        },
        '&.andOrBtn': {
          minWidth: 60,
          height: 28,
          padding: '4px 12px',
          borderRadius: '6px',
          textTransform: 'none',
          fontSize: '13px',
          fontWeight: 500,
          boxShadow: 'none',
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          transition: 'all .4s ease-in-out',
          borderRadius: '4px !important',
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: 'div',
          h2: 'div',
          h3: 'div',
          h4: 'div',
          h5: 'div',
          h6: 'div',
          subtitle1: 'div',
          subtitle2: 'div',
          body1: 'span',
          body2: 'span',
        },
        color: 'text.primary',
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        variant: 'outlined',
        clearOnEscape: true,
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '4px',
          },
        },
        paper: {
          marginTop: 5,
        },
        option: {
          fontWeight: 400,
          margin: '5px',
          minHeight: '40px !important',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          '.Mui-focused': {
            borderWidth: '2px',
            borderStyle: 'solid',
            borderRadius: '4px',
          },
        },
        inputRoot: {
          minHeight: 36,
          // height:"auto !important",
          padding: '0 8px',
          display: 'flex',
          alignItems: 'center',

          //for keeping the end adornment (clear and popup icons) vertically alligned witht the endedornments of the textfeild and date picker
          '& .MuiAutocomplete-endAdornment': {
            right: '16px !important',
          },

          '& .MuiAutocomplete-input': {
            padding: '8px 4px',
          },

          '&.MuiInputBase-sizeSmall': {
            minHeight: 32,

            '& .MuiAutocomplete-input': {
              padding: '4px 4px',
              fontSize: 12,
            },
          },
        },
      },
    },
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: '4px',
          '&.ai-autofill-notification': {
            width: '320px',
            boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.16)',
            borderRadius: '4px',
            overflow: 'hidden',
            pointerEvents: 'auto',
          },
          '&.ai-suggestion-card': {
            borderLeft: '3px solid',
            borderRadius: '4px',
            padding: '12px',
            marginBottom: '8px',
            transition: 'all 0.2s ease-in-out',
            '&.rejected': {
              opacity: 0.55,
            },
          },
          // ReactFlow Landscape – node card structural styles
          '&.landscape-entity-node': {
            display: 'flex',
            flexDirection: 'column',
            borderWidth: '1.5px',
            borderStyle: 'solid',
            borderRadius: '4px',
            width: '133px',
            height: 'auto',
            padding: '8px',
            gap: '8px',
          },
          '&.landscape-lineage-node': {
            display: 'flex',
            flexDirection: 'column',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderRadius: '8px',
            width: '133px',
            height: '70px',
            padding: '8px',
            gap: '8px',
          },
          '&.landscape-cluster-node': {
            display: 'flex',
            flexDirection: 'column',
            borderWidth: '0.5px',
            borderStyle: 'solid',
            borderRadius: '4px',
            width: '262px',
            height: 'auto',
            padding: '8px',
            gap: '12px',
          },
          '&.activity-block-card': {
            width: '100%',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderRadius: '4px',
          },
          '&.landscape-title-card': {
            height: 'auto',
            width: '267px',
            display: 'flex',
            flexDirection: 'column',
            borderWidth: '1px',
            borderStyle: 'solid',
            padding: '16px',
            gap: '16px',
            overflow: 'auto',
          },
          '&.landscape-relation-tooltip': {
            padding: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            minWidth: '200px',
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        select: {
          padding: '0px 12px',
          display: 'block',
          alignItems: 'center',
          textOverflow: 'ellipsis',
          // borderBottom: BORDER_NONE_IMPORTANT,
          // boxShadow: 'none',
          whiteSpace: 'nowrap',
          borderRadius: '4px',
          '&:focus': {
            borderRadius: '4px',
          },
          '&:before': {
            borderBottom: BORDER_NONE_IMPORTANT,
          },
          '&:after': {
            borderBottom: BORDER_NONE_IMPORTANT,
          },
          '.MuiInputBase-sizeSmall &': {
            paddingTop: '2px',
            paddingBottom: '2px',
            fontSize: '14px',
          },
        },
        icon: {
          top: '50%',
          transform: 'translateY(-50%)',
          right: '12px',
        },
        root: {
          height: '36px !important',
          borderRadius: '4px',
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          fontWeight: 400,
          fontSize: 14,
          lineHeight: '125%',
          '&.MuiInputBase-sizeSmall': {
            height: '36px',
            minHeight: '36px',
          },
          // ReactFlow Landscape – template (variant=standard) and entity (variant=outlined) Selects
          '&.landscape-template-select': {
            flexShrink: 0,
            maxWidth: '140px',
            // Override the global theme's forced 36px height for compact header use
            height: 'auto !important',
            minHeight: 'unset',
            backgroundColor: 'transparent !important',
            border: 'none !important',
            '& .MuiSelect-select': {
              paddingTop: '2px',
              paddingBottom: '2px',
              paddingRight: '20px !important',
              fontSize: '10.2px !important',
              fontWeight: '400 !important',
              lineHeight: '15.3px !important',
              letterSpacing: '0px',
              backgroundColor: 'transparent !important',
            },
            '& .MuiSelect-icon': {
              fontSize: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              right: '0px',
            },
            '& .MuiInput-underline:before': { borderBottom: 'none' },
            '& .MuiInput-underline:after': { borderBottom: 'none' },
            '&:hover .MuiInput-underline:before': { borderBottom: 'none' },
          },
          '&.landscape-entity-select': {
            width: '100%',
            borderRadius: '6px',
            boxShadow: '0px 1px 1px rgba(0, 0, 0, 0.05)',
            '& .MuiSelect-icon': {
              fontSize: '16px',
            },
            '& .MuiSelect-select': { padding: '8px' },
          },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '58px !important',
          borderBottom: 'none !important',
          '&.app-header-toolbar': {
            minHeight: '52px !important',
            height: '52px',
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          '&:before': {
            borderBottom: BORDER_NONE_IMPORTANT,
          },
          '&:after': {
            borderBottom: BORDER_NONE_IMPORTANT,
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          borderWidth: '1px',
          borderStyle: 'solid',
          borderRadius: '4px',
          '&.Mui-focused': {
            borderWidth: '2px',
          },
          '&:hover:not(.Mui-disabled):before': {
            borderBottom: BORDER_NONE_IMPORTANT,
          },
          '&:after': {
            borderBottom: BORDER_NONE_IMPORTANT,
          },
          '&:before': {
            borderBottom: BORDER_NONE_IMPORTANT,
          },
        },
        underline: {
          '& :before': {
            borderBottom: BORDER_NONE_IMPORTANT,
          },
          '& :after': {
            borderBottom: BORDER_NONE_IMPORTANT,
          },
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            pointerEvents: 'auto',
          },
          '&.app-header-icon-btn': {
            width: '34px',
            height: '34px',
            borderRadius: '8px',
            padding: '6px',
            transition: 'background-color 150ms ease, color 150ms ease',
          },
          '&:focus-visible': {
            borderRadius: '30%',
            outlineOffset: '1px',
            outlineWidth: '2px',
            outlineStyle: 'solid',
            '& .MuiTouchRipple-root': {
              display: 'none',
            },
          },
          '&.outlined': {
            cursor: 'pointer',
            width: '40px',
            display: 'flex',
            alignItems: 'center',
            borderRadius: '4px',
            border: '1px solid',
          },
          '&.ai-action-accept': {
            width: '28px',
            height: '28px',
            border: '1.5px solid',
            transition: 'all 0.2s ease',
          },
          '&.ai-action-reject': {
            width: '28px',
            height: '28px',
            border: '1.5px solid',
            transition: 'all 0.2s ease',
          },
          '&.ai-action-undo': {
            width: '26px',
            height: '26px',
            border: '1.5px solid',
            transition: 'all 0.2s ease',
          },
          '&.ai-action-accept-sm': {
            width: '20px',
            height: '20px',
            border: '1px solid',
            transition: 'all 0.2s ease',
          },
          '&.ai-action-reject-sm': {
            width: '20px',
            height: '20px',
            border: '1px solid',
            transition: 'all 0.2s ease',
          },
          '&.ai-action-undo-sm': {
            width: '20px',
            height: '20px',
            border: '1px solid',
            transition: 'all 0.2s ease',
          },
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: {
          fontWeight: 400,
          borderRadius: '4px',
        },
        input: {
          '&::placeholder': {
            opacity: '0.7 !important',
            fontSize: '14px !important',
          },
        },
      },
    },
    // for the label
    MuiInputLabel: {
      styleOverrides: {
        root: {
          top: '-18px !important',
          fontWeight: '500',
          lineHeight: '125%',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        autoComplete: 'off',
        slotProps: {
          inputLabel: {
            shrink: true,
          },
        },
      },
      styleOverrides: {
        root: {
          marginTop: '22px',
          '&.solytics-input-field': {
            marginTop: 0,
          },
          borderRadius: '4px',
          '&.no-label': {
            marginTop: '0px',
          },
          '& input.MuiInputBase-input': {
            padding: '8px 12px',
          },
          '& .MuiOutlinedInput-root.MuiInputBase-multiline': {
            height: 'auto !important',
            padding: '8px 12px',
          },
          '& .MuiInputLabel-root': {
            transform: 'none',
            top: '-18px',
            fontWeight: '500',
            lineHeight: '125%',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            top: '-1px',
            legend: {
              display: 'none',
            },
          },

          '& .MuiOutlinedInput-root': {
            borderRadius: '4px',
            height: '36px !important',
            fontWeight: 400,
            fontSize: 14,
            lineHeight: '125%',
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          fontSize: '14px',
          fontWeight: '500',
          height: '36px !important',
          minWidth: 'fit-content',
          borderRadius: '4px',
          boxShadow: BORDER_NONE_IMPORTANT,
          '&.Mui-disabled': {
            pointerEvents: 'auto',
          },
          textTransform: 'capitalize',
          padding: '10px 16px',
          '&:focus-visible': {
            borderRadius: '4px',
            outlineOffset: '2px',
            outlineWidth: '2px',
            outlineStyle: 'solid',
          },
          '& .MuiButton-endIcon': {
            marginLeft: '4px',
            marginRight: '0px',
            marginTop: '2px',
          },
          '&.MenuButtons': {
            fontSize: '13.5px',
            fontWeight: 500,
            padding: '4px 10px',
            height: '32px',
            borderRadius: '6px',
            letterSpacing: '0.01em',
            '& .MuiButton-startIcon': {
              marginRight: '4px',
            },
            '& .MuiButton-endIcon': {
              marginLeft: '2px',
            },
          },
          '&.ai-upload-button': {
            minWidth: 'unset',
            minHeight: '24px',
            height: '24px !important',
            padding: '2.8px 8px',
            textTransform: 'none',
            fontSize: '11px',
            fontWeight: 600,
          },
          '&.ai-autofill-trigger-button': {
            textTransform: 'none',
          },
          '&.ai-dialog-button': {
            textTransform: 'none',
          },
          '&.ai-action-pill': {
            minWidth: 'unset',
            minHeight: '22px',
            height: '22px !important',
            padding: '2px 6px',
            borderRadius: '999px',
            fontSize: '10px',
            fontWeight: 600,
            lineHeight: 1,
            textTransform: 'none',
            '& .MuiButton-startIcon': {
              marginRight: '4px',
              marginLeft: 0,
              '& svg': {
                fontSize: '13px',
              },
            },
          },
          '&.log-filter-standalone-btn': {
            width: '100%',
            justifyContent: 'flex-start',
            padding: '8px 10px',
            fontWeight: 400,
            textTransform: 'none',
            fontSize: '14px',
            borderRadius: '4px',
          },
        },
        sizeSmall: {
          padding: '4px 12px',
          fontSize: '14px',
          height: '32px',
        },
        sizeExtraSmall: {
          padding: '2px 8px',
          fontSize: '14px',
          height: '24px',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: 12,
          fontWeight: 400,
          lineHeight: '19.07px',
          marginLeft: '0px',
          marginTop: '2px',
          paddingLeft: '0px',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontSize: `14px !important`,
          borderWidth: '1px',
          borderStyle: 'solid',
          borderRadius: '4px',
          '&.Mui-focused': {
            borderWidth: '2px',
          },
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: '42px',
          minWidth: 'fit-content',
          borderBottom: '1px solid',
          borderRadius: '4px',
          '& .MuiTabs-scrollButtons': {
            paddingBottom: '0px !important',
          },
          '&.variant-chip-tabs': {
            border: 'none !important',
            '& .MuiTabs-list': {
              marginTop: '2px',
              gap: '8px',
            },
            '& .MuiTabs-indicator': {
              display: 'none', // Hide the default indicator since we're using background color
            },
          },
          '&.ai-filter-tabs': {
            minHeight: 'auto',
            border: 'none',
            borderTop: '1px solid',
            borderBottom: '1px solid',
            borderRadius: 0,
            '& .MuiTabs-list': {
              gap: 0,
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            },
          },
          '&.mi-edit-entity-tabs .MuiTabs-list': {
            gap: '12px',
          },
          '&.mi-edit-entity-tabs .MuiTab-root': {
            minWidth: 'auto',
          },
        },
        list: {
          gap: '8px',
        },
        indicator: {
          height: '2px',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '12px',
          borderRadius: '4px',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          fontWeight: 500,
          lineHeight: '150%',
          textTransform: 'capitalize',
          paddingTop: 0,
          paddingBottom: '0px',
          paddingLeft: '8px',
          paddingRight: '8px',
          flexDirection: 'initial',
          display: 'flex',
          justifyContent: 'center',
          minWidth: '72px',
          minHeight: 36,
          // adding this so that it does not block the tooltip when disabled
          '&.Mui-disabled': {
            pointerEvents: 'auto',
          },
          '&:focus-visible': {
            outlineWidth: '2px',
            outlineStyle: 'solid',
          },
          '&.Mui-selected': {
            borderRadius: '4px',
          },
          '&.vertical-tab-with-icons': {
            minHeight: '25px',
            marginBottom: '5px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '5px !important',
            margin: '2px',
            transition: 'background-color 0.2s ease-in-out',
          },
          '&.vertical-tab': {
            padding: '16px 24px',
            width: '100%',
            maxWidth: 'unset',
            display: 'flex',
            justifyContent: 'flex-start',
            fontSize: '14px',
            borderBottom: '1px solid',
            transition: 'background-color 0.2s ease-in-out',
          },
          '&.horizontal-tab-vertical-style': {
            padding: '8px 12px',
            display: 'flex',
            justifyContent: 'center',
            fontSize: '14px',
            transition: 'background-color 0.2s ease-in-out',
          },
          '&.ai-filter-tab': {
            flex: 1,
            minWidth: 'auto',
            minHeight: 'auto',
            padding: '6px 0',
            textAlign: 'center',
            textTransform: 'none',
            fontSize: '0.7rem',
            fontWeight: 500,
            borderBottom: '2px solid transparent',
            borderLeft: '1px solid',
            transition: 'all 0.2s ease',
            '&:first-of-type': {
              borderLeft: 'none',
            },
            '&.Mui-selected': {
              fontWeight: 700,
              borderBottom: '2px solid',
            },
          },
          '&.variant-chip-tab': {
            margin: '0 4px',
            minHeight: '32px',
            minWidth: 'auto',
            padding: '0 16px',
            borderRadius: '4px',
            textTransform: 'none',
            fontSize: '14px',
            fontWeight: 400,
            transition: 'all 0.3s ease',

            '&:hover': {
              opacity: 1,
            },
            '&.Mui-selected': {
              borderRadius: '4px',
            },
          },
          '&.custom-tab': {
            width: 'fit-content',
            height: 32,
            borderRadius: 4,
            opacity: 1,
            gap: 10,
            padding: '6px 24px',
            minHeight: 32,
            textTransform: 'none',
            '&.Mui-selected': {
              borderRadius: 6,
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          padding: '2px 2px 2px 2px !important',
          lineHeight: 2,
          height: '40px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          userSelect: 'none',
          fontSize: '12px !important',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          textTransform: 'capitalize',
          '&:last-child': {
            borderRadius: '0 4px 0 0',
          },
          '&:first-of-type': {
            borderRadius: '4px 0 0 0',
          },
          '&.list-table-header': {
            borderBottom: 0,
            fontWeight: 600,
            justifyContent: 'flex-start',
          },
          borderBottom: 0,
          border: 'none',
        },
        root: {
          borderBottom: '1px solid ',
          minHeight: 10,
          fontWeight: 400,
          fontSize: '16px !important',
          '&.list-table-cell': {
            display: 'flex !important',
            justifyContent: 'flex-start',
            alignItems: 'center !important',
            borderBottom: '1px solid ',
            minHeight: 10,
          },
        },
        sizeMedium: {
          '&.list-table-cell': {
            lineHeight: 1,
            padding: '8px !important',
          },
          lineHeight: 1,
          padding: '0px 0px 0px 14px !important',
        },
        sizeSmall: {
          padding: '7px 7px 7px 7px !important',
          borderRight: 'solid 1px',
          wordBreak: 'break-all',
          '&:last-child': {
            paddingRight: 4,
            borderRight: 'none',
          },
          input: {
            fontFamily: 'Inter, sans-serif',
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableSortLabel-root': {
            padding: 0,
            border: 'none',
            cursor: 'pointer',
            marginLeft: '4px',
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          borderLeft: 0,
          borderRight: 0,
          borderBottom: 0,
          borderTop: 0,
          borderStyle: 'solid',
          '&.list-table-body': {
            borderLeft: 0,
            borderRight: 0,
            borderBottom: 0,
            borderTop: 0,
            borderStyle: 'solid',
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          // '& .MuiTableBody-root *:hover::-webkit-scrollbar': {
          //   width: '10px !important',
          // },
          borderRadius: '4px',
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          width: '100%',
          display: 'block',
          borderBottom: 0,
          borderRadius: '4px',
          '& p': {
            margin: 0,
          },
        },
        caption: {
          userSelect: 'none',
        },
        select: {
          borderRadius: '4px',
          paddingX: 0,
          fontSize: '14px',
          '&.MuiSelect-select': {
            marginRight: '8px',
          },
          '&:focus-visible': {
            outline: '2px solid !important',
          },
        },
        actions: {
          '& button:focus-visible': {
            outline: '2px solid !important',
            outlineOffset: '2px !important',
          },
        },
        toolbar: {
          minHeight: '58px !important',
          paddingLeft: '11px !important',
          paddingRight: '11px !important',
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          '&:focus-visible': {
            outline: `2px solid !important`,
            outlineOffset: '1px !important',
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: '4px',
          '&.ai-review-drawer': {
            width: '380px',
            boxSizing: 'border-box',
            top: 0,
            height: '100%',
            borderRadius: 0,
            display: 'flex',
            flexDirection: 'column',
          },
          '&.filter-drawer': {
            width: '650px',
            boxSizing: 'border-box',
            top: 0,
            height: '100%',
            borderRadius: 0,
            display: 'flex',
            flexDirection: 'column',
          },
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        maxWidth: 'sm',
        fullWidth: true,
      },
      styleOverrides: {
        paper: {
          padding: 0,
          borderRadius: '4px',
          // padding: "22.05px",
          '& .mui-dialog-close-icon-btn': {},
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: '16px',
          fontStyle: 'normal',
          lineHeight: 'normal',
          padding: '16px 24px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          paddingTop: '24px',
          paddingBottom: '24px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          // justifyContent: 'center',
          paddingTop: '12px',
          paddingBottom: '10px',
          paddingRight: '24px',
          paddingLeft: '24px',
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          width: '25px',
          height: '25px',
          minHeight: '15px',
          boxShadow: 'none',
          zIndex: 0,
        },
      },
    },
    MuiFormControl: {
      defaultProps: {
        variant: 'filled',
      },
      styleOverrides: {
        root: {
          minWidth: '100%',
          display: 'flex !important',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: 12,
          fontWeight: 400,
          fontStyle: 'normal',
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          marginLeft: 0,
          marginRight: 0,
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          paddingBottom: '0px',
          paddingTop: '0px',
          borderRadius: '4px',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '& .MuiListItemButton-root': {
            padding: '16px',
            height: '36px',
            borderRadius: '4px',
            '& .list-item-button-hover-actions-right': {
              display: 'none',
              position: 'absolute',
              right: '0',
            },
            '&:hover': {
              '& .list-item-button-hover-actions-right': {
                display: 'block',
              },
            },
          },
          '&.explorer-list-item': {
            cursor: 'pointer',
            borderRadius: '4px',
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          fontWeight: 600,
          boxShadow: 'none',
          borderRadius: '4px',
          marginRight: '4px',
          selected: {},
          padding: '10px 16px',
          '&:hover': {
            cursor: 'pointer',
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          height: '8px',
          '&.ai-progress': {
            height: '4px',
            borderRadius: '2px',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          // border: 0,
          // borderRadius: '4px !important',
          // minHeight: 28,
          // height: 28,
          // '&:focus-visible': {
          //   outlineOffset: '2px',
          //   outlineWidth: '2px',
          //   outlineStyle: 'solid',
          // },
          '& .MuiChip-deleteIcon': {
            fontSize: '16px',
          },
          '&.file-chip': {
            padding: '0px 4px',
            borderRadius: '4px',
            justifyContent: 'start',
            '& .MuiChip-label': {
              flexGrow: 1,
              width: '100%',
              maxWidth: 'unset',
            },
            '& .MuiChip-deleteIcon': {
              marginRight: 0,
              marginLeft: '8px',
            },
          },
          '&.draft-chip': {
            minHeight: '16px',
            height: '16px',
            padding: '0 6px',
            borderRadius: '4px',
            cursor: 'default',
            '& .MuiChip-label': {
              padding: 0,
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: '16px',
              fontFamily: 'Inter, sans-serif',
              textAlign: 'center',
            },
          },
          '&.draft-values-count-chip': {
            minHeight: '24px',
            display: 'flex',
            height: '24px',
            padding: '2px 8px',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '4px',
            '& .MuiChip-label': {
              textAlign: 'center',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '20px',
            },
            '& .MuiChip-icon': {
              width: '12px',
              height: '12px',
              aspectRatio: '1/1',
              '& svg': {
                width: '12px',
                height: '12px',
              },
            },
          },
          '&.draft-state-header-chip': {
            minHeight: '32px',
            display: 'flex',
            height: '24px',
            padding: '2px 8px',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '2px',
            borderRadius: '4px',

            '& .MuiChip-label': {
              textAlign: 'center',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '20px',
            },
            '& .MuiChip-icon': {
              width: '12px',
              height: '12px',
              aspectRatio: '1/1',
              '& svg': {
                width: '12px',
                height: '12px',
              },
            },
          },
          '&.sm-chip': {
            minHeight: '18px',
            height: '18px',
            padding: '0 6px',
            borderRadius: '4px',
            pointerEvents: 'none',
            '& .MuiChip-label': {
              padding: 0,
              fontSize: '11px',
              fontWeight: 500,
              lineHeight: '16px',
            },
          },
          '&.md-chip': {
            minHeight: '22px',
            padding: '0 6px',
            borderRadius: '4px',
            pointerEvents: 'none',
            '& .MuiChip-label': {
              padding: 0,
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '16px',
            },
          },
          '&.thread-chip': {
            padding: '4px 8px',
            minHeight: '22px',
            borderRadius: '4px',
            '& .MuiChip-label': {
              padding: 0,
              fontSize: '12px',
              fontWeight: 500,
            },
          },
          '&.user-chip': {
            padding: '4px 8px',
            height: 'auto',
            minHeight: 'unset',
            borderRadius: '4px',
            justifyContent: 'start',
            gap: '4px',
            '& .MuiChip-label': {
              maxWidth: 'unset',
              padding: 0,
              whiteSpace: 'normal',
              fontSize: '12px',
              fontWeight: 400,
            },
            '& .MuiChip-icon': {
              maxWidth: 'unset',
              margin: 0,
              fontSize: '14px',
              alignSelf: 'start',
              marginTop: '3px',
            },
            cursor: 'default',
          },
          '&.filter-chip': {
            borderRadius: '4px',
            height: '36px',
            fontSize: '0.85rem',
            fontWeight: 600,
            marginRight: '8px',
            width: '175px',
            '& .MuiChip-label': {
              fontWeight: 600,
            },
          },
          // advance Chips for headers like "Having" / "Where"
          '&.advance-header-chip': {
            borderRadius: '4px',
            height: '40px',
            width: '170px',

            '& .MuiChip-label': {
              fontSize: '14px',
              fontWeight: '400',
            },
          },
          '&.ai-status-chip': {
            height: '22px',
            minHeight: '22px',
            border: 'none',
            '& .MuiChip-label': {
              fontSize: '11px',
              fontWeight: 600,
            },
            '& .MuiChip-icon': {
              color: 'inherit',
              fontSize: '14px',
            },
          },
          '&.ai-badge-chip': {
            height: 'auto',
            minHeight: 'auto',
            borderRadius: '10px !important',
            border: 'none',
            padding: '4px 6px',
            '& .MuiChip-label': {
              padding: 0,
              fontSize: '10px !important',
              fontWeight: 600,
            },
            '& .MuiChip-icon': {
              color: 'inherit',
              fontSize: '11px',
              marginLeft: 0,
              marginRight: '4px',
            },
          },
          '&.ai-section-chip': {
            height: '18px',
            minHeight: '18px',
            padding: '12px',
            pointerEvents: 'none',
            '& .MuiChip-label': {
              padding: 0,
              fontSize: '10px',
              fontWeight: 700,
            },
          },
        },
        label: {
          fontSize: '12px !important',
          fontWeight: '400 !important',
          lineHeight: '133.333% !important',
          fontStyle: 'normal',
          // maxWidth: '180px',
        },
        colorSecondary: {
          borderWidth: '1px',
          borderStyle: 'solid',
        },
      },
      variants: [
        {
          props: { variant: 'outlined' },
          style: {
            '& .MuiChip-label': {
              fontWeight: 600,
            },
            border: '2px solid',
            padding: '0px 4px',
            display: 'inline-flex',
            alignItems: 'center',
          },
        },
        {
          props: { variant: 'customFilter' },
          style: {
            borderRadius: '4px',
            border: '1px solid',
            height: '36px',
            padding: '0 8px',
            display: 'flex',
            alignItems: 'center',
            '& .MuiChip-deleteIcon': {
              fontSize: 'medium',
              borderRadius: '4px',
              border: '1px solid',
            },
          },
        },
      ],
    },
    MuiPopover: {
      styleOverrides: {
        root: {
          '& .MuiPopover-paper': {
            borderRadius: '4px',
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          '& .MuiSvgIcon-root': {
            fontSize: '20px',
          },
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          '& .MuiSvgIcon-root': {
            fontSize: '20px',
          },
          '&.Mui-focusVisible': {
            borderRadius: '50%',
            '& .MuiSvgIcon-root': {
              borderRadius: '2px',
            },
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        track: {
          height: '14px !important',
          width: '34px !important',
          borderRadius: '7px',
        },
        thumb: {
          height: '20px !important',
          width: '20px !important',
          border: '1px solid',
        },
        input: {
          '&:focus-visible + .MuiSwitch-thumb': {
            outlineWidth: '2px',
            outlineStyle: 'solid',
          },
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          height: '4px',
        },
        track: {
          border: '1px solid',
        },
        rail: {
          border: '1px solid',
        },
        mark: {
          height: '4px',
          width: '4px',
          borderRadius: '2px',
        },
        valueLabel: {
          fontSize: '14px',
          lineHeight: '20px',
          fontWeight: 400,
          letterSpacing: '0.25px',
        },
        markLabel: {
          fontSize: '10px',
          lineHeight: '20px',
          fontWeight: '600 !important',
          letterSpacing: '0.25px',
        },
        thumb: {
          height: 20,
          width: 20,
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: '44px',
          maxHeight: '44px',
          borderRadius: '4px',
          display: 'flex',
          padding: '0 16px',
          '&.Mui-expanded': {
            minHeight: '44px',
            maxHeight: '44px',
          },
          '&.ai-section-summary': {
            minHeight: 'auto',
            maxHeight: 'none',
            padding: '6.8px 16px',
            borderRadius: 0,
            position: 'sticky',
            top: '-1px',
            zIndex: 2,
            backdropFilter: 'blur(8px)',
            transition: 'all 0.2s ease',
            borderBottom: '1px solid',
            borderLeft: '2px solid',
            '&.Mui-expanded': {
              minHeight: 'auto',
              maxHeight: 'none',
            },
            '& .MuiAccordionSummary-expandIconWrapper': {
              transition: 'transform 0.2s ease',
            },
            '& .MuiAccordionSummary-content': {
              margin: '0',
              '&.Mui-expanded': {
                margin: '0',
              },
            },
          },
        },
        content: {
          margin: '12px 0',
          '&.Mui-expanded': {
            margin: '12px 0',
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          width: '100%',
          paddingLeft: 0,
          paddingRight: 0,
          paddingTop: 0,
          paddingBottom: 0,
          '&.ai-section-details': {
            padding: '6px 0 0 12px',
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          border: 'none',
          boxShadow: 'none',
          marginBottom: '16px',
          borderRadius: '4px',
          width: '100%',
          '&.permission-accordion': {
            marginBottom: '0px',
            borderRadius: '0px',
          },
          '&.job-queue-accordion': {
            border: '1px solid',
          },
          '&.ai-section-accordion': {
            marginBottom: '16px',
            borderRadius: 0,
            border: 'none',
            boxShadow: 'none',
            '&:before': {
              display: 'none',
            },
            '&:first-of-type': {
              marginTop: '12px',
            },
          },
          '&:accordian-before': {
            display: 'none',
            height: '0px',
          },
          '&.log-filter-accordion': {
            '&.Mui-expanded:first-of-type': {
              marginTop: '8px',
              borderRadius: '4px',
            },
            '&:first-of-type': {
              borderRadius: '4px',
            },
            '&.Mui-expanded:last-of-type': {
              marginTop: '8px',
              borderRadius: '4px',
            },
            '&:last-of-type': {
              borderRadius: '4px',
            },
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {},
        message: {
          display: 'flex',
          alignItems: 'center',
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          color: 'text.primary',
          '& a': {
            color: 'inherit',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          },
        },
        separator: {
          marginLeft: '4px',
          marginRight: '4px',
        },
      },
    },
    SolyticsEditorToolbar: {
      styleOverrides: {
        baseButton: {
          width: 30,
          height: 30,
          minWidth: 0,
          borderRadius: '4px',
          border: 'none',
          display: 'grid',
          placeContent: 'center',
        },
        toggleButton: {
          width: 30,
          height: 30,
          minWidth: 0,
          borderRadius: '4px',
          border: 'none',
          display: 'grid',
          placeContent: 'center',
        },
        headingDropdown: {
          border: 'none',
          minWidth: 0,
          height: 30,
          marginTop: 0,
          justifyContent: 'center',
          '.MuiOutlinedInput-notchedOutline': {
            display: 'none',
          },
          '.MuiSelect-select': {
            padding: '2px 8px !important',
            minWidth: 100,
            fontWeight: 400,
          },
          '.MuiOutlinedInput-root': {
            height: '100%',
          },
          '.MuiSelect-icon': {
            right: '8px',
          },
        },
        ToolbarPaper: {
          transform: 'translateY(10px)',
        },
        ToolbarMenu: {
          '.MuiPaper-root': {
            transform: 'translateY(10px)',
          },
        },
        ToolbarMenuItem: {
          fontSize: 12,
          fontWeight: 400,
          gap: 1,
          ':hover': {
            fontWeight: 400,
          },
        },
        ToolbarVerticalDivider: {},
      },
    },
    BubbleMenuContainer: {
      styleOverrides: {
        root: {
          flexDirection: 'row',
          padding: 1,
          gap: 1,
          borderRadius: '4px',
        },
      },
    },
    BubbleMenuButton: {
      styleOverrides: {
        root: {
          padding: '2px',
          minWidth: 0,
          minHeight: 0,
          borderRadius: '4px',
          border: 'none',
          textTransform: 'none',
          fontSize: 12,
        },
      },
    },
    TemplatesSectionsAndAttributes: {
      styleOverrides: {
        section: {
          border: '1px solid transparent',
          borderRadius: '4px',
          overflow: 'hidden',
          padding: '16px',
          '&.isOverAndOrdering': {
            border: '1px solid',
          },
          '&.isOrdering': {
            ':hover': {
              border: '1px solid',
            },
          },
          '&:hover': {
            border: '1px solid transparent',
          },
        },
      },
    },
    BulkTransitionHistory: {
      styleOverrides: {
        statusCircle: {},
        listItemContainer: {
          '& :hover': {
            cursor: 'pointer',
            borderRadius: '4px',
          },
        },
        itemWrapper: {
          borderRadius: '4px',
        },
        list: {
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        },
        deletedModelBox: {
          borderRadius: '4px',
        },
      },
    },
    WorkflowSidebar: {
      styleOverrides: {
        conditionalCard: {
          clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
          width: '50%',
          minWidth: '100px',
          maxWidth: 130,
          maxHeight: 130,
        },
        conditionalStack: {
          aspectRatio: 1,
          padding: 2,
          justifyContent: 'center',
          alignItems: 'center',
          clipPath: 'polygon(50% 1%, 99% 50%, 50% 99%, 1% 50%)',
          border: '1px solid',
          filter: 'drop-shadow(0px 0px black)',
          maxWidth: 130,
          maxHeight: 130,
        },
        cardTypography: {
          whiteSpace: 'nowrap',
          wordBreak: 'unset',
          textWrap: 'wrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          textAlign: 'center',
          maxHeight: '2.2rem',
          WebkitLineClamp: '2',
          maxWidth: 130,
        },
        nodeCard: {
          borderWidth: '1px',
          borderStyle: 'solid',
          display: 'flex',
          flexGrow: 1,
          borderRadius: 8,
          maxWidth: '130px',
          cursor: 'grab',
        },
        nodeIconBox: {
          padding: 8,
        },
        nodeContentBox: {
          paddingLeft: 1,
          paddingRight: 1,
          paddingTop: 0.5,
          paddingBottom: 0.5,
          margin: 0,
          width: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
        },
        nodeTypography: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      },
    },
    FormulaSidebar: {
      styleOverrides: {
        nodeCard: {
          borderWidth: '1px',
          borderStyle: 'solid',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexGrow: 1,
          borderRadius: '4px',
          maxWidth: '200px',
          cursor: 'grab',
          padding: '8px',
          paddingBottom: '8px',
        },
        container: {
          height: '62vh',
          padding: '16px',
          overflow: 'auto',
          width: 'max-content',
        },
        buttonContainer: {
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          marginBottom: '24px',
        },
        nodesContainer: {
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          '& .MuiPaper-root': {
            paddingBottom: '8px',
          },
        },
      },
    },
    Login: {
      styleOverrides: {
        SuccessAlert: {
          width: '100%',
          marginBottom: '16px',
        },
        ErrorAlert: {
          width: '100%',
          marginBottom: '16px',
        },
      },
    },
    MultiUploadFormDropzoneBox: {
      styleOverrides: {
        root: {
          height: '100px',
          borderRadius: '4px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
      },
    },
  },
  shape: {
    borderRadius: 8,
  },
};
export default theme;
